// React
import { ReactElement } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Context
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useUser } from '@utmb/contexts/AuthContext';
import { toast } from 'react-toastify';
// Modules
import { useFormik } from 'formik';
import * as Yup from 'yup';
// Interfaces
import { DashboardModalFormProps } from '@utmb/types/dashboard-modal';
import UserMemberUpdateStatusView from '@utmb/types/view/UserMemberUpdateStatusView';
// Design System
import { InputMessage } from '@utmb/design-system/input-message/InputMessage';
import { Input } from '@utmb/design-system/input/Input';
// Features
import { ActionsButtons } from '@utmb/features/user-edit-wrapper/ActionsButtons';
// Utils
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
// Styles
import styles from './user-account-settings-form.module.scss';

export const UserAccountChangePasswordForm = (props: DashboardModalFormProps): ReactElement | null => {
    const { onCancel } = props;

    const { t } = useTranslation();
    const { dashboardMember, dashboardOrganizer } = useDashboard();
    const { me } = useUser();

    const noInputError = t('contactForm.noInputError');

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            currentPassword: '',
            newPassword: '',
            newPasswordConfirmation: '',
        },
        validationSchema: Yup.object({
            currentPassword: Yup.string().required(noInputError),
            newPassword: Yup.string().required(noInputError),
            newPasswordConfirmation: Yup.string()
                .oneOf([Yup.ref('newPassword'), null], t('myutmb:myUtmb.myInformation.accountSettings.notSamePassword'))
                .required(noInputError),
        }),
        onSubmit: async (values) => {
            if (values.newPassword !== values.newPasswordConfirmation) {
                toast.error(`Passwords are not the same !`);
                return;
            }
            const response = (await fetchTheAPI({
                isClient: true,
                path: '/users/sso/change-password',
                method: FetchMethodsEnum.POST,
                body: { ...values, email: me!.email },
            })) as UserMemberUpdateStatusView;

            if (response === null) {
                toast.success(t('myutmb:myUtmb.myInformation.successMessage'));
                onCancel();
            } else {
                toast.error(`${t('myutmb:myUtmb.myInformation.errorMessage')} : ${response ? response.message : ''}`);
            }
        },
    });

    if (!dashboardMember && !dashboardOrganizer) {
        return null;
    }

    return (
        <form onSubmit={formik.handleSubmit} className={styles.form}>
            <div className={`${styles.input_container} ${styles.password}`}>
                <Input
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    label={t('myutmb:myUtmb.myInformation.accountSettings.oldPassword')}
                    placeholder={t('myutmb:myUtmb.myInformation.accountSettings.oldPassword')}
                    value={formik.values.currentPassword}
                    onchange={formik.handleChange}
                    onblur={formik.handleBlur}
                    require={true}
                />
                {formik.touched.currentPassword && formik.errors.currentPassword && <InputMessage type="error" message={formik.errors.currentPassword} />}
            </div>
            <div className={`${styles.input_container} ${styles.password}`}>
                <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    label={t('myutmb:myUtmb.myInformation.accountSettings.newPassword')}
                    placeholder={t('myutmb:myUtmb.myInformation.accountSettings.newPassword')}
                    value={formik.values.newPassword}
                    onchange={formik.handleChange}
                    onblur={formik.handleBlur}
                    require={true}
                />
                {formik.touched.newPassword && formik.errors.newPassword && <InputMessage type="error" message={formik.errors.newPassword} />}
            </div>
            <div className={`${styles.input_container} ${styles.password}`}>
                <Input
                    id="newPasswordConfirmation"
                    name="newPasswordConfirmation"
                    type="password"
                    label={t('myutmb:myUtmb.myInformation.accountSettings.newPasswordConfirmation')}
                    placeholder={t('myutmb:myUtmb.myInformation.accountSettings.newPasswordConfirmation')}
                    value={formik.values.newPasswordConfirmation}
                    onchange={formik.handleChange}
                    onblur={formik.handleBlur}
                    require={true}
                />
                {formik.touched.newPasswordConfirmation && formik.errors.newPasswordConfirmation && (
                    <InputMessage type="error" message={formik.errors.newPasswordConfirmation} />
                )}
            </div>

            <ActionsButtons onCancel={onCancel} />
        </form>
    );
};
